import type { ImageBleed, PAGE_POSITIONS } from 'types';
import type { ImageWithMetadata } from 'types/capi/article';

import ChevronIcon from '~/assets/icons/Chevron';
import AffiliatePageLink from '~/components/AffiliatePageLink';
import Image from '~/components/Image';
import ImageMetadata, { LinkType } from '~/components/ImageMetadata';
import Tracked from '~/components/Tracked';
import { PLP_SEGMENTS } from '~/utils/navigationContext';

export default function ImageWithMetadata({
  bleed,
  index,
  item,
  pagePosition = 'Image Grid'
}: {
  bleed?: ImageBleed;
  index: number;
  item: ImageWithMetadata;
  pagePosition?: PAGE_POSITIONS;
}) {
  const { cta, desktop, mobile, product } = item;

  const productImage =
    product && 'image' in product ? product.image : undefined;
  const imageSrc = mobile.url || productImage?.url || '';
  const imageAlt = mobile.alt || productImage?.alt || `image-${index}`;
  const desktopSrc = desktop.url || productImage?.url || '';
  const linkType = cta?.path ? deriveLinkTypeFromPath(cta.path) : 'product';

  const content = (
    <div className="flex flex-col gap-3.5">
      <div className="relative">
        <Image
          alt={imageAlt}
          src={imageSrc}
          display={bleed === 'full' ? 'full-bleed' : undefined}
          responsiveOptions={{
            md: { src: desktopSrc },
            xs: { src: imageSrc }
          }}
          progressiveLoadSettings={{
            highResQuality: 100,
            lowResQuality: 50
          }}
          className="w-full"
          loading="eager"
        />

        <ResponsiveCaption
          desktopCaption={desktop.caption}
          mobileCaption={mobile.caption}
          reserveCtaSpace={!!product}
        />

        {product && <ShopNowOverlay />}
      </div>

      {(product || cta) && (
        <ImageMetadata
          affiliate={
            product?.type !== 'internal'
              ? (product?.affiliate ??
                (cta?.type !== 'internal' ? cta?.affiliate : undefined))
              : undefined
          }
          bleed={bleed}
          linkType={linkType}
          price={product?.price}
          title={product?.name ?? cta?.text ?? ''}
        />
      )}
    </div>
  );

  if (product?.type === 'internal') {
    return (
      <Tracked.InternalLink
        to={product.path}
        name={`FEATURES_${product.name}`}
        pagePosition={pagePosition}
        variant="none"
      >
        {content}
      </Tracked.InternalLink>
    );
  }

  if (
    product &&
    (product.type === 'external' || product.type === 'inline-external')
  ) {
    if (product.affiliate) {
      return (
        <AffiliatePageLink
          urlTemplates={product}
          buttonName={`FEATURES_${product.name}`}
          pagePosition={pagePosition}
          variant="none"
          affiliate={product.affiliate}
        >
          {content}
        </AffiliatePageLink>
      );
    }

    return (
      <Tracked.ExternalLink
        href={product.url}
        target="_blank"
        rel="sponsored nofollow noopener"
        name={`FEATURES_${product.name}`}
        pagePosition={pagePosition}
        variant="none"
      >
        {content}
      </Tracked.ExternalLink>
    );
  }

  if (cta?.type === 'internal') {
    return (
      <Tracked.InternalLink
        to={cta.path}
        name={`FEATURES_${cta.text}`}
        pagePosition={pagePosition}
        variant="none"
      >
        {content}
      </Tracked.InternalLink>
    );
  }

  if (cta) {
    return (
      <AffiliatePageLink
        urlTemplates={cta}
        buttonName={`FEATURES_${cta.text}`}
        pagePosition={pagePosition}
        variant="none"
        affiliate={cta.affiliate}
      >
        {content}
      </AffiliatePageLink>
    );
  }

  return content;
}

function ShopNowOverlay() {
  return (
    <span className="absolute bottom-0 right-0 flex h-6 items-center gap-1 bg-black px-2 text-xs font-semibold leading-5 tracking-[0.24px] text-grey-tile">
      Shop now
      <span className="rotate-270">
        <ChevronIcon stroke="#CFCFCF" width="8" height="8" />
      </span>
    </span>
  );
}

function ResponsiveCaption({
  desktopCaption,
  mobileCaption,
  reserveCtaSpace
}: {
  desktopCaption?: string;
  mobileCaption?: string;
  reserveCtaSpace?: boolean;
}) {
  if (!desktopCaption && !mobileCaption) return null;

  const captionClass = `absolute bottom-3 left-3 text-sm font-semibold text-white text-shadow-outline${
    reserveCtaSpace ? ' right-24' : ''
  }`;

  if (desktopCaption === mobileCaption || !desktopCaption) {
    return <p className={captionClass}>{mobileCaption}</p>;
  }

  if (!mobileCaption) {
    return <p className={captionClass}>{desktopCaption}</p>;
  }

  return (
    <>
      <p className={`${captionClass} md:hidden`}>{mobileCaption}</p>
      <p className={`${captionClass} hidden md:block`}>{desktopCaption}</p>
    </>
  );
}

function deriveLinkTypeFromPath(path: string): LinkType {
  const segments = path.split('/').filter(Boolean);

  if (segments.some((s) => ['features', 'articles', 'news'].includes(s)))
    return 'article';

  const plpSegmentIndex = segments.findIndex((s) => PLP_SEGMENTS.includes(s));

  if (plpSegmentIndex !== -1) {
    return segments.length - plpSegmentIndex - 1 >= 3 ? 'product' : 'plp';
  }

  return 'external';
}
